import React, { memo, useCallback, useEffect, useRef } from "react";
import { IDashboardContext, useDashboardContext } from "../../contexts/dashboard-context";
import { usePreviewContext } from "../../contexts/preview-context";
import { MenuIds, useMenus } from "../../hooks/useMenus";
import { useIsTransformMode } from "../../hooks/useIsTransformMode";
import { useMessage } from "../../hooks/useMessage";
import { useSelectedZone } from "../../hooks/useSelection";
import { useStableCallback } from "../../hooks/useStableCallback";
import { transformToFrame } from "../../transform";
import { IPanCropOverlayRef, PanCropOverlay } from "./pan-crop-overlay";
import { usePhotoZooming } from "../slideshow-preview/hooks/use-photo-zooming";

function canEnableTransformMode(
	dashboard: IDashboardContext,
	selectedZone: eXpress.core.IZone | undefined,
) {
	return dashboard.canPanPhoto(selectedZone) || dashboard.canZoomPhoto(selectedZone);
}

/** Overlay on top of Preview that we can use to draw extra elements during photo panning/zooming */
export const PanCropOverlayContainer = memo(() => {
	/** Ref to DOM element */
	const panCropOverlayRef = useRef<IPanCropOverlayRef>(null);
	const selectedZone = useSelectedZone();
	const previewContext = usePreviewContext();
	const { slideShowPreviewRef } = usePreviewContext();
	const [isTransformMode, setIsTransformMode] = useIsTransformMode();
	const dashboard = useDashboardContext();
	const {
		actions: { setMenu },
	} = useMenus();

	const photoLookZone = eXpress.core.isPhotoLookZone(selectedZone) ? selectedZone : undefined;
	const [{ canZoomIn, canZoomOut }, photoZooming] = usePhotoZooming();

	// eslint-disable-next-line @typescript-eslint/no-deprecated
	const onWheel = useStableCallback((e: WheelEvent) => {
		if (!isTransformMode) return;
		// Quickfix
		// When pinching on touchpad, or Ctrl + wheel: wheel is handled by usePhotoPinching
		// and we will skip this code
		if (e.ctrlKey) return;

		if (!photoLookZone) return;
		e.preventDefault();
		const isZoomOut = e.deltaY > 0;
		if ((isZoomOut && !canZoomOut) || (!isZoomOut && !canZoomIn)) return;

		const screenToSpreadTransform = previewContext.getScreenToSpreadTransform(
			photoLookZone.spread,
			undefined,
		);
		const crop = photoLookZone.getPhotoLook()?.getCrop();
		let pivot: eXpress.math.Point | undefined;
		if (screenToSpreadTransform && crop) {
			const screenPoint = eXpress.math.point(e.clientX, e.clientY);
			const screenToSpread = transformToFrame(screenToSpreadTransform);
			const spreadToZone = photoLookZone.spreadTransform.inverse();
			const zoneToImage = crop.croppedImageRectToZoneFrame.inverse();

			pivot = screenPoint
				.mul(screenToSpread)
				.mul(spreadToZone)
				.mul(zoneToImage)
				.clamp(crop.croppedImageRect); // Clamp to cropRect or image?
		}

		photoZooming.start({ selectedZone: photoLookZone });
		photoZooming.update(1.05, isZoomOut, pivot);
		photoZooming.end();
	});

	useEffect(() => {
		const el = slideShowPreviewRef.current?.previewEl;
		if (!el) return undefined;
		el.addEventListener("wheel", onWheel, { passive: false });
		return () => {
			el.removeEventListener("wheel", onWheel);
		};
	}, [slideShowPreviewRef, onWheel]);

	const updateOverlay = useCallback(
		function updateOverlay() {
			const panCropOverlay = panCropOverlayRef.current;
			if (!panCropOverlay) return;

			// Update when TransformMode changes
			const screenToSpreadDipTransform = previewContext.getSpreadToScreenTransform(
				selectedZone?.spread,
				undefined,
			);
			if (isTransformMode && screenToSpreadDipTransform && photoLookZone) {
				panCropOverlay.updateOverlay(
					photoLookZone,
					transformToFrame(screenToSpreadDipTransform),
				);
			} else {
				panCropOverlay.updateOverlay(undefined);
			}
		},
		[isTransformMode, previewContext, selectedZone, photoLookZone],
	);

	// We can use useMessage because we don't depend on react state
	useMessage(
		// eslint-disable-next-line @typescript-eslint/no-deprecated
		useStableCallback((message) => {
			if (
				eXpress.core.isPhotoSizeOrPositionChangedInZone(message) &&
				message.affects(selectedZone)
			) {
				updateOverlay();
			}
		}),
		{ captureInteractiveMessages: true },
	);

	useEffect(() => {
		// Update on TransformMode state change
		updateOverlay();

		// Follow preview, listen to Preview Zoom/Pinches
		const slideShow = slideShowPreviewRef.current?.slideShow;
		if (!slideShow) return undefined;
		const unsubscribe = slideShow.onTransformChange.subscribe(() => {
			updateOverlay();
		});

		return () => {
			unsubscribe();
		};
	}, [dashboard, selectedZone, setIsTransformMode, slideShowPreviewRef, updateOverlay]);

	useMessage(
		useCallback(
			(message) => {
				// Example undo of filling a photoZone
				// The SwapPhotos commando handles the zone selection behaviour (isSwapPhotosMessage == false => skip)
				if (
					eXpress.core.isPhotoInExistingZoneChanged(message) &&
					!eXpress.core.isSwapPhotosMessage(message)
				) {
					if (!canEnableTransformMode(dashboard, selectedZone)) {
						if (!isTransformMode) return;
						setIsTransformMode(false);
						setMenu(MenuIds.PhotoSelection);
					}
				}
			},
			[dashboard, setMenu, isTransformMode, selectedZone, setIsTransformMode],
		),
	);

	return <PanCropOverlay ref={panCropOverlayRef} />;
});
PanCropOverlayContainer.displayName = "PanCropOverlayContainer";
